import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setProgress } from "../../redux/slice/system";
import { usePop, useHookForm } from "../hooks";

import { ModuleTablePaginator } from "../../service/table";
import { FormCheckbox } from "../formElement";
import { system_table } from "../../service/system";
import { decimalAddZero } from "../../service/common";

import { classNames } from "primereact/utils";
import { DataTable } from "primereact/datatable";
import { ColumnGroup } from "primereact/columngroup";
import { Column } from "primereact/column";
import { Row } from "primereact/row";
import { Button } from "primereact/button";

const useTable = ({
  initData = [],
  initSize = system_table.size,
  isExport = true,
  isFilter = false,
  filterColumns = {},
} = {}) => {
  const dispatch = useDispatch();
  const [tbData, setTbData] = useState(initData);
  const [tbTotalRecords, setTbTotalRecords] = useState(0);
  const [tbFirstIndex, setTbFirstIndex] = useState(0);
  const [tbRows, setTbRows] = useState(initSize);
  const [tbColumns, setTbColumns] = useState([]);
  const [tbHeadGroup, setTbHeadGroup] = useState({
    columns: null,
    group: null,
  });
  const [tbFootGroup, setTbFootGroup] = useState({
    columns: null,
    group: null,
  });
  const [tbCellClass, setTbCellClass] = useState(null);
  const [tbExpandRows, setTbExpandRows] = useState(null);
  const [tbExpandColumns, setTbExpandColumns] = useState({});
  const [pageAction, setPageAction] = useState(null);
  const { isPop, openPop, closePop, renderDialog } = usePop([
    {
      key: "tbFilter",
      title: () => "資料篩選",
      isOpen: false,
    },
  ]);

  // 標題欄位展開更多
  const handleTbExpand = ({ key }) => {
    setTbExpandColumns((state) => ({
      ...state,
      [key]: !state[key],
    }));
  };

  const onPage = (e) => {
    const startIndex = e.first,
      rows = e.rows;

    // 同一頁面，不刷新
    if (startIndex === tbFirstIndex && rows === tbRows) return;

    setTbFirstIndex(startIndex);
    setTbRows(rows);

    if (pageAction) pageAction({ startIndex, rows });
  };

  const createTbHeader = ({ template = null, columns }) => {
    let group = template;
    if (template === null) {
      group = (
        <ColumnGroup>
          {columns.map((rows) => (
            <Row key={rows.key}>
              {rows.data.map((column) => (
                <Column
                  key={`${rows.key}_${column.key}`}
                  className={classNames({
                    hidden: column.header === null,
                  })}
                  {...column}
                />
              ))}
            </Row>
          ))}
        </ColumnGroup>
      );
    }
    setTbHeadGroup({ columns, group });
  };

  const createTbFooter = ({ template = null, columns }) => {
    let group = template;
    if (template === null) {
      group = (
        <ColumnGroup>
          {columns.map((rows) => (
            <Row key={rows.key}>
              {rows.data.map((column) => (
                <Column
                  key={`${rows.key}_${column.key}`}
                  {...column}
                  footer={
                    typeof column.footer === "number"
                      ? decimalAddZero({
                          rawVal: column.footer,
                          decimalShowRaw: true,
                        })
                      : column.footer
                  }
                />
              ))}
            </Row>
          ))}
        </ColumnGroup>
      );
    }

    setTbFootGroup({ columns, group });
  };

  const createTbCellClass = (value, cellData) => {
    if (tbCellClass === null) return;

    let field = cellData.column.props.field;
    return {
      "bg-tb-subtitle": (tbCellClass?.subTitle || [])?.indexOf(field) !== -1,
      "bg-tb-thirdtitle":
        (tbCellClass?.thirdTitle || [])?.indexOf(field) !== -1,
    };
  };

  const renderTable = useCallback(
    ({
      className = "",
      nullTransfer = "",
      isLazy = true,
      isPaginator = true,
      options = {},
    } = {}) => {
      return (
        <>
          <DataTable
            value={tbData}
            className={classNames(className)}
            size="small"
            paginator={isPaginator}
            paginatorTemplate={ModuleTablePaginator}
            headerColumnGroup={tbHeadGroup.group}
            footerColumnGroup={tbFootGroup.group}
            cellClassName={createTbCellClass}
            lazy={isLazy}
            first={tbFirstIndex}
            totalRecords={tbTotalRecords}
            onPage={onPage}
            rows={tbRows}
            emptyMessage={system_table.empty}
            {...options}
          >
            {tbColumns?.map((col) => (
              <Column key={`${col.field}_${col.header}`} {...col} />
            ))}
          </DataTable>

          {renderDialog({
            className: "w-11 lg:w-6 md:w-8 sm:w-10",
            children: (
              <>
                {isPop.tbFilter?.isOpen && (
                  <ModulePopTbFilter closePrePop={closePop} />
                )}
              </>
            ),
          })}
        </>
      );
    },
    [
      tbColumns,
      tbData,
      tbHeadGroup,
      tbFootGroup,
      tbCellClass,
      tbFirstIndex,
      tbTotalRecords,
      tbRows,
      isPop,
    ]
  );

  function ModulePopTbFilter({ closePrePop }) {
    const defaultValues = {
      isFrozen: true,
    };
    const { handleSubmit, reset, setting } = useHookForm({
      defaultValues,
    });
    const [tbFilter, setTbFilter] = useState({});

    useEffect(() => {
      resetSetting();
    }, []);

    const resetSetting = () => {
      let temp = null;
      reset(temp);
      setTbFilter(temp);
    };

    const onSubmit = (submitData) => {
      // let setting = localStorage.getItem("tbFilter") ?? "{}";
      // setting = JSON.parse(setting);
      // setTbFilter(submitData);
      // closePrePop({ type: "tbFilter" });
    };

    return (
      <>
        <div className="px-3">
          <form onSubmit={handleSubmit(onSubmit)} className="formgrid grid">
            <div className="col-12 text-right mt-3">
              <Button
                type="button"
                label="取消"
                className="p-button-secondary p-button-outlined ml-auto px-3"
                onClick={() => {
                  resetSetting();
                  closePrePop({ type: "tbFilter" });
                }}
              />
              <Button type="submit" label="確認" className="ml-2 px-3" />
            </div>
          </form>
        </div>
      </>
    );
  }

  return {
    ColumnGroup,
    Column,
    Row,
    tbData,
    setTbData,
    tbTotalRecords,
    setTbTotalRecords,
    tbFirstIndex,
    setTbFirstIndex,
    tbRows,
    setTbRows,
    tbColumns,
    setTbColumns,
    tbExpandRows,
    setTbExpandRows,
    tbExpandColumns,
    handleTbExpand,
    setTbHeadGroup,
    setTbFootGroup,
    createTbHeader,
    createTbFooter,
    setTbCellClass,
    renderTable,
    setPageAction,
  };
};

export default useTable;
